import classNames from 'classnames';
import { Link } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import {
  IS_PRO,
  REGION,
  RegionEnum,
} from 'gatsby-theme-hem-store/src/constants';
import { Button, Text, TextColor, TextVariant } from 'hem-ui';
import React from 'react';

import { BackgroundImage } from '../BackgroundImage';
import { MarketingCommunicationForms } from '../MarketingCommunicationForms';
import {
  buttons,
  grid,
  image,
  inner,
  inner2,
  legal,
  links,
  logo,
  newsletter,
  wrapper,
} from './Footer.module.scss';
import { aboutLinks, complianceLinks, supportLinks, tradeLinks } from './links';
import { PaymentOptions } from './PaymentOptions';

export const Footer: React.FC<{ isHomePage: boolean }> = ({
  isHomePage,
}): JSX.Element => {
  return (
    <footer className={classNames(wrapper, 'no-print')}>
      <div className={grid}>
        {!isHomePage && REGION !== RegionEnum.Ca ? (
          <BackgroundImage
            className={image}
            darken
            staticAsset={
              <StaticImage
                style={{ gridArea: '1 / 1' }}
                src="footer-image.jpg"
                alt=""
                quality={100}
                height={380}
              />
            }
          >
            <div className={inner}>
              <div className={inner2}>
                <Text
                  tag="p"
                  variant={TextVariant.TITLE}
                  color={TextColor.WHITE}
                >
                  Design professional? Join Hem Pro
                </Text>
                <Text
                  tag="p"
                  variant={TextVariant.BODY_SMALL}
                  color={TextColor.WHITE}
                >
                  Hem Pro is our dedicated trade program for design
                  professionals, giving you instant access to a trade discount
                  starting at 25%, free material samples, and real-time stock
                  levels.
                </Text>
              </div>
              <div className={buttons}>
                <Button
                  to="/hem-pro"
                  color="white"
                  aria-label="Learn more about Hem Pro"
                >
                  Learn more
                </Button>
              </div>
            </div>
          </BackgroundImage>
        ) : (
          <hr
            style={{
              gridColumn: '1 / -1',
              marginBottom: 'calc(var(--spacing-l) * 2)',
            }}
          />
        )}

        <nav className={links}>
          {React.Children.toArray(
            [aboutLinks, supportLinks, tradeLinks].map((o) => (
              <ul>
                <li>
                  <Text tag="p" variant={TextVariant.TITLE}>
                    {o.title}
                  </Text>
                </li>
                {React.Children.toArray(
                  o.links
                    .filter((link) => {
                      if (
                        process.env.GATSBY_REGION_REGION_DIFFERENTIATOR ===
                          'CA B2C' &&
                        link.title === 'Hem Pro'
                      ) {
                        return false;
                      }
                      return true;
                    })
                    .filter((link) => !!link?.url)
                    .map((link) => (
                      <li>
                        {link.url.startsWith('http') ? (
                          <Text
                            tag="a"
                            variant={TextVariant.BODY_SMALL}
                            href={link.url}
                          >
                            {link.title}
                          </Text>
                        ) : (
                          <Link to={link.url}>
                            <Text variant={TextVariant.BODY_SMALL}>
                              {link.title}
                            </Text>
                          </Link>
                        )}
                      </li>
                    )),
                )}
              </ul>
            )),
          )}
        </nav>

        <div className={newsletter}>
          <MarketingCommunicationForms
            form="both"
            newsletterTopTitle="Weekly inbox inspiration"
            newsletterTitle="Sign up to the newsletter!"
            newsletterContent={
              IS_PRO
                ? 'Get updates on sales and campaigns, exclusive pre-access to product launches, and weekly inspiration.'
                : 'Get a 10% discount on your first order, updates on sales and campaigns, exclusive pre-access to product launches, and weekly inspiration.'
            }
            smsTopTitle="Let’s exchange numbers!"
            smsTitle="Exclusive SMS membership"
            smsContent="Enter your mobile number to get an additional 15% off one purchase, exclusive membership deals, early access to product launches and more!"
            location="Footer"
          />
        </div>

        <nav className={legal}>
          <Text variant={TextVariant.BODY_X_SMALL} color={TextColor.GRAY_DARK}>
            © {new Date().getFullYear()} Hem, All rights reserved
          </Text>

          <PaymentOptions />

          <ul>
            {complianceLinks.map((link) => (
              <li key={link.url}>
                <Link to={link.url}>
                  <Text
                    variant={TextVariant.BODY_X_SMALL}
                    color={TextColor.GRAY_DARK}
                  >
                    {link.title}
                  </Text>
                </Link>
              </li>
            ))}
            {REGION === RegionEnum.Us ? (
              <li>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (typeof window !== 'undefined') {
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      window.revisitCkyConsent();
                    }
                  }}
                >
                  <Text
                    variant={TextVariant.BODY_X_SMALL}
                    color={TextColor.GRAY_DARK}
                  >
                    Do not sell my personal information
                  </Text>
                </a>
              </li>
            ) : null}
          </ul>
        </nav>
      </div>

      <div className={logo}>
        <svg viewBox="0 0 46 16">
          <path
            d="M41.738 3.752c-1.773 0-2.947.806-3.753 2.003-.69-1.243-1.98-2.003-3.591-2.003-1.427 0-2.624.668-3.27 1.796V4.052H28.34v11.603h2.878V8.978c0-1.542.99-2.555 2.28-2.555 1.334 0 2.186.967 2.186 2.51v6.722h2.901V8.978c0-1.542.967-2.555 2.28-2.555 1.358 0 2.186.967 2.186 2.51v6.722h2.878V8.127c0-2.579-1.911-4.375-4.19-4.375zM20.88 6.17c1.658 0 2.67.898 3.04 2.371h-6.102c.392-1.45 1.497-2.371 3.062-2.371zm5.917 3.499c0-3.522-2.486-5.94-5.94-5.94-3.453 0-5.962 2.74-5.962 6.124 0 3.407 2.578 6.147 6.17 6.147 2.394 0 4.282-1.105 5.317-2.74l-2.256-1.427c-.575.944-1.634 1.612-3.038 1.612-1.681 0-2.993-1.06-3.338-2.74h8.955c.046-.184.092-.645.092-1.036zM10.245 0v6.239H3.108V0H0v15.655h3.108v-6.63h7.137v6.63h3.108V0h-3.108z"
            fill="#212427"
          />
        </svg>
      </div>
    </footer>
  );
};

export default Footer;
